/**
 * ContextLogsTable 上下文日志列表组件
 * 功能：分页展示上下文日志摘要，支持刷新、搜索过滤和查看详情
 * 详情弹窗包含：完整请求信息、响应头、curl/Python/JS 调用示例
 * 修改时间：2026-06-09
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess } from '../helpers';
import { Button, Form, Modal, Pagination, Table, Icon } from 'semantic-ui-react';

const ITEMS_PER_PAGE = 10;

const statusOptions = [
  { key: 'all', value: '', text: '全部状态' },
  { key: 'success', value: 'success', text: '成功' },
  { key: 'failed', value: 'failed', text: '失败' },
];

/**
 * 格式化 JSON 字符串
 * @param {string|Object} str - 原始 JSON 字符串或对象
 * @returns {string} 格式化后的 JSON
 */
const formatJSON = (str) => {
  if (!str) return '';
  if (typeof str === 'object') return JSON.stringify(str, null, 2);
  try { return JSON.stringify(JSON.parse(str), null, 2); } catch { return str; }
};

/**
 * 解析请求头对象
 * @param {string|Object} header - 请求头字符串或对象
 * @returns {Object} 解析后的请求头
 */
const parseHeaders = (header) => {
  if (!header) return {};
  if (typeof header === 'object') return header;
  try { return JSON.parse(header); } catch { return {}; }
};

/**
 * 构建 curl 命令
 * @param {Object} log - 日志数据
 * @returns {string} curl 命令
 */
const buildCurl = (log) => {
  const url = log.request_url || '';
  const headers = parseHeaders(log.request_header);
  const body = formatJSON(log.request_body);
  const apiKey = log.token_name || 'YOUR_API_KEY';
  let cmd = `curl -X POST '${url}' \\\n`;
  cmd += `  -H 'Content-Type: application/json' \\\n`;
  cmd += `  -H 'Authorization: Bearer ${apiKey}' \\\n`;
  // 添加其他重要 header
  for (const [key, value] of Object.entries(headers)) {
    const k = key.toLowerCase();
    if (k === 'content-type' || k === 'authorization') continue;
    cmd += `  -H '${key}: ${value}' \\\n`;
  }
  if (body) {
    cmd += `  -d '${body}'`;
  }
  return cmd;
};

/**
 * 构建 Python 调用示例
 * @param {Object} log - 日志数据
 * @returns {string} Python 代码
 */
const buildPython = (log) => {
  const url = log.request_url || '';
  const headers = parseHeaders(log.request_header);
  const body = formatJSON(log.request_body);
  const apiKey = log.token_name || 'YOUR_API_KEY';
  let code = `import requests\n\n`;
  code += `url = "${url}"\n`;
  code += `headers = {\n`;
  code += `    "Content-Type": "application/json",\n`;
  code += `    "Authorization": "Bearer ${apiKey}",\n`;
  for (const [key, value] of Object.entries(headers)) {
    const k = key.toLowerCase();
    if (k === 'content-type' || k === 'authorization') continue;
    code += `    "${key}": "${value}",\n`;
  }
  code += `}\n`;
  if (body) {
    code += `payload = ${body}\n\n`;
  } else {
    code += `payload = {}\n\n`;
  }
  code += `response = requests.post(url, json=payload, headers=headers)\n`;
  code += `print(response.json())`;
  return code;
};

/**
 * 构建 JavaScript 调用示例
 * @param {Object} log - 日志数据
 * @returns {string} JavaScript 代码
 */
const buildJavaScript = (log) => {
  const url = log.request_url || '';
  const headers = parseHeaders(log.request_header);
  const body = formatJSON(log.request_body);
  const apiKey = log.token_name || 'YOUR_API_KEY';
  let code = `const url = "${url}";\n\n`;
  code += `const headers = {\n`;
  code += `  "Content-Type": "application/json",\n`;
  code += `  "Authorization": "Bearer ${apiKey}",\n`;
  for (const [key, value] of Object.entries(headers)) {
    const k = key.toLowerCase();
    if (k === 'content-type' || k === 'authorization') continue;
    code += `  "${key}": "${value}",\n`;
  }
  code += `};\n\n`;
  if (body) {
    code += `const payload = ${body};\n\n`;
  }
  code += `fetch(url, {\n`;
  code += `  method: "POST",\n`;
  code += `  headers,\n`;
  code += `  body: JSON.stringify(payload),\n`;
  code += `})\n`;
  code += `  .then(res => res.json())\n`;
  code += `  .then(data => console.log(data))\n`;
  code += `  .catch(err => console.error(err));`;
  return code;
};

/**
 * 复制到剪贴板
 * @param {string} text - 要复制的文本
 */
const copyToClipboard = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => showSuccess('已复制到剪贴板')).catch(() => showError('复制失败'));
};

/**
 * 从响应头解析下载文件名
 * @param {string} disposition - Content-Disposition 响应头
 * @param {string} fallback - 解析失败时使用的文件名
 * @returns {string} 文件名
 */
const parseFileName = (disposition, fallback) => {
  if (!disposition) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim()); } catch { /* 继续尝试普通匹配 */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : fallback;
};

// 统一样式常量（与全局样式保持一致）—— 深空黑 + 紫蓝霓虹
const styles = {
  card: {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    padding: '20px 24px',
    marginBottom: 20,
    border: '1px solid rgba(0,0,0,0.08)',
  },
  searchSection: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '14px 16px',
    marginBottom: 16,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  },
  tableRow: {
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
    background: 'transparent',
  },
  tableRowHover: {
    transform: 'translateY(-1px)',
    background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
    boxShadow: 'inset 2px 0 0 #18181b, 0 8px 20px rgba(0,0,0,0.06)',
  },
  tag: {
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
  },
  button: {
    borderRadius: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'transform 0.16s ease, box-shadow 0.16s ease',
  },
};

const ContextLogsTable = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const downloadLock = useRef(false);
  const [detailModal, setDetailModal] = useState(false);
  const [detailLog, setDetailLog] = useState(null);
  const [spaceUsage, setSpaceUsage] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  // 代码示例选中 tab: 'curl' | 'python' | 'javascript'
  const [codeTab, setCodeTab] = useState('curl');

  // 搜索过滤条件
  const [filters, setFilters] = useState({
    username: '',
    model_name: '',
    start_date: '',
    end_date: '',
    status: '',
  });

  /**
   * 构建查询参数
   * @param {number} page - 页码
   * @param {Object} customFilters - 自定义过滤条件
   * @returns {string} 查询字符串
   */
  const buildQueryString = useCallback((page, customFilters) => {
    const f = customFilters || filters;
    const params = new URLSearchParams();
    params.set('p', page);
    if (f.username) params.set('username', f.username);
    if (f.model_name) params.set('model_name', f.model_name);
    if (f.start_date) params.set('start_date', f.start_date);
    if (f.end_date) params.set('end_date', f.end_date);
    if (f.status) params.set('status', f.status);
    return params.toString();
  }, [filters]);

  /**
   * 加载上下文日志列表
   * @param {number} page - 页码
   * @param {Object} customFilters - 自定义过滤条件
   */
  const loadContextLogs = useCallback(async (page, customFilters) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/log/context?${buildQueryString(page - 1, customFilters)}`);
      const { success, message, data, total } = res.data;
      if (success) {
        setLogs(data || []);
        setTotal(total || 0);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [buildQueryString]);

  /**
   * 加载空间使用情况
   */
  const loadSpaceUsage = async () => {
    try {
      const res = await API.get('/api/log/context/space');
      const { success, data } = res.data;
      if (success) setSpaceUsage(data);
    } catch (_) { /* 静默 */ }
  };

  /**
   * 查看详情
   * @param {string} requestId - 请求ID
   */
  const viewDetail = async (requestId) => {
    try {
      const res = await API.get(`/api/log/context/${requestId}`);
      const { success, message, data } = res.data;
      if (success) {
        setDetailLog(data);
        setDetailModal(true);
        setCodeTab('curl'); // 重置 tab
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    }
  };

  const handleRefresh = () => { setActivePage(1); loadContextLogs(1); loadSpaceUsage(); };
  const handleClearAll = async () => {
    if (!window.confirm('确认清除所有上下文日志？此操作不可恢复。')) return;
    setClearing(true);
    try {
      const res = await API.delete('/api/log/context/all');
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(`已清理 ${data || 0} 条上下文日志`);
        setActivePage(1);
        await loadContextLogs(1);
        await loadSpaceUsage();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setClearing(false);
    }
  };

  const handleDownloadLogs = async () => {
    if (downloadLock.current) return;
    downloadLock.current = true;
    setDownloading(true);
    try {
      const res = await API.get('/api/log/context/download', {
        responseType: 'blob',
      });
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        let message = '下载失败';
        try {
          const payload = JSON.parse(text);
          if (payload?.message) message = payload.message;
        } catch {
          if (text) message = text;
        }
        showError(message);
        return;
      }

      const fileName = parseFileName(res.headers['content-disposition'], 'context-logs.zip');
      const blob = res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: contentType || 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccess(t('log.studio.download_success', { name: fileName }));
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const payload = JSON.parse(text);
          showError(payload?.message || error.message || t('log.studio.download_failed'));
          return;
        } catch {
          // fall through to generic error
        }
      }
      showError(error.message || t('log.studio.download_failed'));
    } finally {
      downloadLock.current = false;
      setDownloading(false);
    }
  };
  const handleSearch = () => { setActivePage(1); loadContextLogs(1); };
  const handleReset = () => {
    const empty = { username: '', model_name: '', start_date: '', end_date: '', status: '' };
    setFilters(empty);
    setActivePage(1);
    loadContextLogs(1, empty);
  };
  const handleFilterChange = (_, { name, value }) => setFilters((prev) => ({ ...prev, [name]: value }));

  useEffect(() => { loadContextLogs(activePage); loadSpaceUsage(); }, [activePage, loadContextLogs]);

  /**
   * 渲染代码块（带复制按钮）
   * @param {string} code - 代码内容
   * @param {string} lang - 语言标签
   */
  const renderCodeBlock = (code, lang) => (
    <div className='studio-logs-code-block'>
      <span className='studio-logs-code-block__lang'>{lang}</span>
      <pre>{code}</pre>
      <Button
        size='mini'
        icon
        title='复制代码'
        onClick={() => copyToClipboard(code)}
        className='studio-logs-copy-button'
      >
        <Icon name='copy outline' />
      </Button>
    </div>
  );

  return (
    <div className='studio-logs-panel studio-context-logs'>
      <header className='studio-logs-section-head'>
        <div className='studio-logs-section-head__copy'>
          <span className='studio-logs-eyebrow'>CONTEXT ARCHIVE</span>
          <h2>{t('log.studio.context_title')}</h2>
          <p>{t('log.studio.context_subtitle')}</p>
        </div>
        {spaceUsage && (
          <div className={`studio-context-storage${spaceUsage.usage_percent > 80 ? ' is-critical' : ''}`}>
            <div className='studio-context-storage__topline'>
              <span className='studio-context-storage__icon'><Icon name='database' /></span>
              <div>
                <span>{t('log.studio.storage')}</span>
                <strong>{spaceUsage.used_mb.toFixed(2)} / {spaceUsage.max_mb.toFixed(0)} MB</strong>
              </div>
              <span className='studio-context-storage__percent'>{spaceUsage.usage_percent.toFixed(1)}%</span>
            </div>
            <div className='studio-context-storage__track'>
              <span style={{ width: `${Math.min(spaceUsage.usage_percent, 100)}%` }} />
            </div>
            <div className='studio-context-storage__actions'>
              <Button
                size='mini'
                basic
                loading={downloading}
                disabled={downloading || clearing}
                onClick={handleDownloadLogs}
              >
                <Icon name='download' /> {t('log.studio.download_logs')}
              </Button>
              <Button
                size='mini'
                color='red'
                basic
                loading={clearing}
                disabled={clearing || downloading}
                onClick={handleClearAll}
              >
                <Icon name='trash alternate outline' /> {t('log.studio.clear_cache')}
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* 搜索过滤 */}
      <div className='studio-logs-toolbar'>
        <div className='studio-logs-toolbar__heading'>
          <div>
            <span className='studio-logs-eyebrow'>TRACE FILTERS</span>
            <h3>{t('log.studio.context_filter_title')}</h3>
          </div>
          <Icon name='sliders horizontal' />
        </div>
        <Form>
          <Form.Group widths='equal' style={{ marginBottom: 12 }}>
            <Form.Input label='用户名' placeholder='输入用户名' name='username'
              value={filters.username} onChange={handleFilterChange}
              style={{ borderRadius: 8 }} />
            <Form.Input label='模型名称' placeholder='输入模型名称' name='model_name'
              value={filters.model_name} onChange={handleFilterChange}
              style={{ borderRadius: 8 }} />
            <Form.Input label='开始日期' type='date' name='start_date'
              value={filters.start_date} onChange={handleFilterChange}
              style={{ borderRadius: 8 }} />
            <Form.Input label='结束日期' type='date' name='end_date'
              value={filters.end_date} onChange={handleFilterChange}
              style={{ borderRadius: 8 }} />
            <Form.Dropdown label='状态筛选' placeholder='全部状态' fluid selection
              options={statusOptions} name='status' value={filters.status}
              onChange={handleFilterChange} style={{ borderRadius: 8, minWidth: 140 }} />
          </Form.Group>
          <div className='studio-logs-toolbar__actions'>
            <Button primary onClick={handleSearch} loading={loading} style={styles.button}>
              <Icon name='search' /> 搜索
            </Button>
            <Button basic onClick={handleReset} style={styles.button}>
              <Icon name='undo' /> 重置
            </Button>
            <Button onClick={handleRefresh} loading={loading} style={{ ...styles.button, marginLeft: 'auto' }}>
              <Icon name='refresh' /> 刷新
            </Button>
          </div>
        </Form>
      </div>

      {/* 上下文日志表格 */}
      <section className='studio-logs-table-card'>
        <div className='studio-logs-table-card__head'>
          <div>
            <span className='studio-logs-eyebrow'>REQUEST TRACES</span>
            <h3>{t('log.studio.context_records')}</h3>
          </div>
          <span className='studio-logs-record-count'>{t('log.studio.total_count', { count: total })}</span>
        </div>
        <div className='studio-logs-table-scroll'>
      <Table basic='very' size='small' compact className='studio-logs-table studio-context-logs-table'>
        <Table.Header>
          <Table.Row style={{ background: '#f5f5f5' }}>
            <Table.HeaderCell style={{ width: 80, fontWeight: 600, color: '#374151' }}>状态</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 120, fontWeight: 600, color: '#374151' }}>请求 ID</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 80, fontWeight: 600, color: '#374151' }}>用户</Table.HeaderCell>
            <Table.HeaderCell style={{ fontWeight: 600, color: '#374151' }}>模型</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 100, fontWeight: 600, color: '#374151' }}>渠道</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 60, fontWeight: 600, color: '#374151' }}>流式</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 70, fontWeight: 600, color: '#374151' }}>代理</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 80, fontWeight: 600, color: '#374151' }}>耗时</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 160, fontWeight: 600, color: '#374151' }}>时间</Table.HeaderCell>
            <Table.HeaderCell style={{ fontWeight: 600, color: '#374151' }}>错误信息</Table.HeaderCell>
            <Table.HeaderCell style={{ width: 80, fontWeight: 600, color: '#374151' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {logs.map((log, idx) => (
            <Table.Row
              key={idx}
              className={`${hoveredRow === idx ? 'is-hovered ' : ''}${log.status === 'failed' ? 'is-failed' : ''}`.trim()}
              onMouseEnter={() => setHoveredRow(idx)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              {/* 状态 */}
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                {log.status === 'failed' ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: '#fef2f2',
                    color: '#dc2626',
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}>
                    <Icon name='remove' style={{ margin: 0, fontSize: 10 }} /> 失败
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: '#f0fdf4',
                    color: '#16a34a',
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}>
                    <Icon name='checkmark' style={{ margin: 0, fontSize: 10 }} /> 成功
                  </span>
                )}
              </Table.Cell>
              {/* 请求 ID */}
              <Table.Cell>
                <code style={{
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  fontSize: 11,
                  color: '#6b7280',
                  background: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: 4,
                }} title={log.request_id}>
                  {log.request_id ? log.request_id.substring(0, 12) + '…' : '-'}
                </code>
              </Table.Cell>
              {/* 用户 */}
              <Table.Cell style={{ color: '#374151', fontWeight: 500 }}>{log.username || '-'}</Table.Cell>
              {/* 模型 */}
              <Table.Cell>
                {log.model_name ? (
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: 'rgba(0,0,0,0.08)',
                    color: '#18181b',
                    fontSize: 12,
                    fontWeight: 500,
                    border: '1px solid rgba(0,0,0,0.16)',
                  }}>{log.model_name}</span>
                ) : <span style={{ color: '#374151' }}>-</span>}
                {log.actual_model_name && log.actual_model_name !== log.model_name && (
                  <div style={{ marginTop: 2 }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: '#fff7ed',
                      color: '#c2410c',
                      fontSize: 11,
                      fontWeight: 500,
                      border: '1px solid #fed7aa',
                    }}>→ {log.actual_model_name}</span>
                  </div>
                )}
              </Table.Cell>
              {/* 渠道 */}
              <Table.Cell style={{ color: '#6b7280' }}>{log.channel_name || '-'}</Table.Cell>
              {/* 流式 */}
              <Table.Cell textAlign='center'>
                {log.is_stream ? (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: '#faf5ff',
                    color: '#9333ea',
                    fontSize: 11,
                    fontWeight: 500,
                    border: '1px solid #e9d5ff',
                  }}>是</span>
                ) : <span style={{ color: '#374151' }}>否</span>}
              </Table.Cell>
              {/* 代理 */}
              <Table.Cell textAlign='center'>
                {log.proxy_used ? (
                  <span title={`${log.proxy_ip}:${log.proxy_port}`} style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: '#f0fdf4',
                    color: '#16a34a',
                    fontSize: 11,
                    fontWeight: 500,
                    border: '1px solid rgba(22,163,74,0.3)',
                  }}>是</span>
                ) : log.proxy_fallback ? (
                  <span title="代理重试耗尽，已回退直连" style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: '#fffbeb',
                    color: '#d97706',
                    fontSize: 11,
                    fontWeight: 500,
                    border: '1px solid rgba(217,119,6,0.3)',
                  }}>回退</span>
                ) : <span style={{ color: '#374151' }}>否</span>}
              </Table.Cell>
              {/* 耗时 */}
              <Table.Cell>
                {log.elapsed_time ? (
                  <span style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: '#374151',
                    fontWeight: 500,
                  }}>
                    {(log.elapsed_time / 1000).toFixed(2)}s
                  </span>
                ) : <span style={{ color: '#374151' }}>-</span>}
              </Table.Cell>
              {/* 时间 */}
              <Table.Cell style={{ color: '#6b7280', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                {log.created_at_str || '-'}
              </Table.Cell>
              {/* 错误信息 */}
              <Table.Cell
                style={{
                  maxWidth: 250,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: log.status === 'failed' ? '#dc2626' : '#374151',
                  fontSize: 12,
                }}
                title={log.error_message || ''}>
                {log.status === 'failed' ? log.error_message || '-' : '-'}
              </Table.Cell>
              {/* 操作 */}
              <Table.Cell>
                <Button
                  size='mini'
                  basic
                  onClick={() => viewDetail(log.request_id)}
                  style={{
                    ...styles.button,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    minWidth: 64,
                    whiteSpace: 'nowrap',
                    color: '#18181b',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                >
                  <Icon name='eye' style={{ margin: 0, lineHeight: 1 }} />
                  <span>详情</span>
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
          {logs.length === 0 && !loading && (
            <Table.Row>
              <Table.Cell colSpan={11} textAlign='center'>
                <div className='studio-logs-state'>
                  <span className='studio-logs-state__icon'><Icon name='inbox' /></span>
                  <strong>{t('log.studio.context_empty_title')}</strong>
                  <span>{t('log.studio.context_empty_description')}</span>
                </div>
              </Table.Cell>
            </Table.Row>
          )}
          {logs.length === 0 && loading && (
            <Table.Row>
              <Table.Cell colSpan={11} textAlign='center'>
                <div className='studio-logs-state studio-logs-state--loading'>
                  <span className='studio-logs-state__icon'><Icon name='spinner' loading /></span>
                  <strong>{t('log.studio.context_loading_title')}</strong>
                  <span>{t('log.studio.context_loading_description')}</span>
                </div>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={11} style={{ background: '#f5f5f5', borderBottom: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 12, columnGap: 24 }}>
                <span style={{ color: '#6b7280', fontSize: 13 }}>共 <strong style={{ color: '#374151' }}>{total}</strong> 条</span>
                <div style={{ marginLeft: 'auto' }}>
                  <Pagination
                    activePage={activePage}
                    onPageChange={(_, { activePage }) => setActivePage(activePage)}
                    size='small'
                    siblingRange={1}
                    totalPages={Math.ceil(total / ITEMS_PER_PAGE) || 1}
                  />
                </div>
              </div>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
        </div>
      </section>

      {/* ==================== 详情弹窗 ==================== */}
      <Modal
        open={detailModal}
        onClose={() => setDetailModal(false)}
        size='fullscreen'
        closeIcon
        className='studio-modal studio-logs-detail-modal'
      >
        <Modal.Header className='studio-logs-detail-modal__header'>
          <Icon name='file alternate outline' style={{ color: '#18181b', fontSize: 20 }} />
          <span style={{ fontSize: 16, fontWeight: 600, color: '#18181b' }}>上下文日志详情</span>
          {detailLog?.status === 'failed' ? (
            <span style={{
              padding: '4px 12px',
              borderRadius: 6,
              background: '#fef2f2',
              color: '#dc2626',
              fontSize: 12,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <Icon name='remove' style={{ margin: 0, fontSize: 10 }} /> 失败
            </span>
          ) : (
            <span style={{
              padding: '4px 12px',
              borderRadius: 6,
              background: '#f0fdf4',
              color: '#16a34a',
              fontSize: 12,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <Icon name='checkmark' style={{ margin: 0, fontSize: 10 }} /> 成功
            </span>
          )}
        </Modal.Header>
        <Modal.Content scrolling className='studio-logs-detail-modal__content'>
          {detailLog && (
            <div className='studio-logs-detail'>
              {/* 基本信息 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: '2px solid rgba(0,0,0,0.16)',
              }}>
                <Icon name='info circle' style={{ color: '#18181b', margin: 0 }} />
                <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>基本信息</h4>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 12,
                marginBottom: 24,
              }}>
                {[
                  { label: '请求 ID', value: <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{detailLog.request_id}</code> },
                  { label: '用户', value: `${detailLog.username} (ID: ${detailLog.user_id})` },
                  { label: '客户端模型', value: <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(0,0,0,0.08)', color: '#18181b', fontSize: 12, fontWeight: 500, border: '1px solid rgba(0,0,0,0.16)' }}>{detailLog.model_name}</span> },
                  { label: '实际上游模型', value: detailLog.actual_model_name || '-' },
                  { label: '最终渠道', value: `${detailLog.channel_name} (ID: ${detailLog.channel_id})` },
                  { label: '渠道类型', value: detailLog.channel_type ?? '-' },
                  { label: 'API 格式', value: detailLog.api_format || '默认' },
                  { label: '过滤 token 上限字段', value: detailLog.remove_max_tokens ? '是' : '否' },
                  { label: '请求尝试次数', value: detailLog.relay_attempt || 1 },
                  { label: 'Token', value: detailLog.token_name },
                  { label: '流式', value: detailLog.is_stream ? '是' : '否' },
                  { label: '加密', value: detailLog.enable_encryption ? '是' : '否' },
                  {
                    label: '代理分发',
                    value: detailLog.proxy_used
                      ? <span style={{ color: '#16a34a' }}>是 ({detailLog.proxy_ip}:{detailLog.proxy_port})</span>
                      : detailLog.proxy_fallback
                        ? <span style={{ color: '#d97706' }}>代理失败，已回退直连</span>
                        : '否'
                  },
                  { label: '耗时', value: detailLog.elapsed_time ? (detailLog.elapsed_time / 1000).toFixed(2) + 's' : '-' },
                  { label: '时间', value: detailLog.created_at_str },
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: '12px 16px',
                    background: '#f5f5f5',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.10)',
                  }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>{item.label}</div>
                    <div style={{ color: '#18181b', fontWeight: 500 }}>{item.value || '-'}</div>
                  </div>
                ))}
              </div>

              {/* 请求 URL */}
              {detailLog.request_url && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    marginTop: 24,
                  }}>
                    <Icon name='linkify' style={{ color: '#18181b', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>请求 URL</h4>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 24 }}>
                    <pre style={{
                      background: '#f5f5f5', padding: '16px 48px 16px 16px', borderRadius: 10,
                      border: '1px solid rgba(0,0,0,0.10)', wordBreak: 'break-all', maxHeight: 300, overflow: 'auto',
                      fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace", fontSize: 13,
                      lineHeight: 1.6,
                    }}>
                      {detailLog.request_url}
                    </pre>
                    <Button size='mini' icon title='复制' onClick={() => copyToClipboard(detailLog.request_url)}
                      style={{ position: 'absolute', top: 12, right: 12, background: '#ffffff', border: '1px solid rgba(0,0,0,0.10)', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <Icon name='copy outline' />
                    </Button>
                  </div>
                </>
              )}

              {/* 上游地址 */}
              {(detailLog.upstream_request_url || detailLog.request_host) && (
                <div style={{
                  display: 'grid',
                  gap: 8,
                  marginBottom: 24,
                  padding: '12px 16px',
                  background: 'rgba(0,0,0,0.03)',
                  borderRadius: 10,
                  border: '1px solid rgba(0,0,0,0.16)',
                }}>
                  {detailLog.upstream_request_url && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon name='server' style={{ color: '#18181b', margin: 0 }} />
                      <span style={{ fontWeight: 500, color: '#374151' }}>实际上游 URL：</span>
                      <code style={{ fontFamily: "'JetBrains Mono', monospace", color: '#18181b', wordBreak: 'break-all' }}>{detailLog.upstream_request_url}</code>
                    </div>
                  )}
                  {detailLog.request_host && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon name='settings' style={{ color: '#18181b', margin: 0 }} />
                      <span style={{ fontWeight: 500, color: '#374151' }}>渠道 Base URL：</span>
                      <code style={{ fontFamily: "'JetBrains Mono', monospace", color: '#18181b', wordBreak: 'break-all' }}>{detailLog.request_host}</code>
                    </div>
                  )}
                </div>
              )}

              {/* 请求头 */}
              {detailLog.request_header && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    marginTop: 24,
                  }}>
                    <Icon name='heading' style={{ color: '#18181b', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>请求头</h4>
                  </div>
                  <div style={{
                    background: '#f5f5f5',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.10)',
                    overflow: 'hidden',
                    marginBottom: 24,
                  }}>
                    {Object.entries(parseHeaders(detailLog.request_header)).map(([key, value], i) => (
                      <div key={key} style={{
                        display: 'flex',
                        borderBottom: i < Object.entries(parseHeaders(detailLog.request_header)).length - 1 ? '1px solid rgba(0,0,0,0.10)' : 'none',
                      }}>
                        <div style={{
                          width: 200,
                          padding: '10px 16px',
                          background: '#f5f5f5',
                          fontWeight: 600,
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          color: '#18181b',
                          borderRight: '1px solid rgba(0,0,0,0.10)',
                        }}>{key}</div>
                        <div style={{
                          flex: 1,
                          padding: '10px 16px',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          wordBreak: 'break-all',
                          color: '#374151',
                        }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* 响应头 */}
              {detailLog.response_header && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    marginTop: 24,
                  }}>
                    <Icon name='reply' style={{ color: '#18181b', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>响应头</h4>
                  </div>
                  <div style={{
                    background: '#f5f5f5',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.10)',
                    overflow: 'hidden',
                    marginBottom: 24,
                  }}>
                    {Object.entries(parseHeaders(detailLog.response_header)).map(([key, value], i) => (
                      <div key={key} style={{
                        display: 'flex',
                        borderBottom: i < Object.entries(parseHeaders(detailLog.response_header)).length - 1 ? '1px solid rgba(0,0,0,0.10)' : 'none',
                      }}>
                        <div style={{
                          width: 200,
                          padding: '10px 16px',
                          background: '#f5f5f5',
                          fontWeight: 600,
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          color: '#18181b',
                          borderRight: '1px solid rgba(0,0,0,0.10)',
                        }}>{key}</div>
                        <div style={{
                          flex: 1,
                          padding: '10px 16px',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          wordBreak: 'break-all',
                          color: '#374151',
                        }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* 错误信息（仅失败时显示） */}
              {detailLog.status === 'failed' && detailLog.error_message && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    marginTop: 24,
                  }}>
                    <Icon name='exclamation triangle' style={{ color: '#dc2626', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#dc2626', fontWeight: 600 }}>错误信息</h4>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 24 }}>
                    <pre style={{
                      background: '#fef2f2', padding: '16px 48px 16px 16px', borderRadius: 10,
                      border: '1px solid rgba(220,38,38,0.3)', maxHeight: 320, overflow: 'auto', fontSize: 13,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      color: '#dc2626', lineHeight: 1.6,
                    }}>
                      {detailLog.error_message}
                    </pre>
                    <Button size='mini' icon title='复制' onClick={() => copyToClipboard(detailLog.error_message)}
                      style={{ position: 'absolute', top: 12, right: 12, background: '#ffffff', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 6 }}>
                      <Icon name='copy outline' />
                    </Button>
                  </div>
                </>
              )}

              {/* 请求体 */}
              {detailLog.request_body && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    marginTop: 24,
                  }}>
                    <Icon name='arrow right' style={{ color: '#d97706', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>
                      {detailLog.relay_attempt !== undefined ? '客户端原始请求体' : '请求体（旧日志未区分原始与上游）'}
                    </h4>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 24 }}>
                    <pre style={{
                      background: '#fffbeb', padding: '16px 48px 16px 16px', borderRadius: 10,
                      border: '1px solid rgba(217,119,6,0.3)', maxHeight: 400, overflow: 'auto', fontSize: 13,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                      lineHeight: 1.6,
                    }}>
                      {formatJSON(detailLog.request_body)}
                    </pre>
                    <Button size='mini' icon title='复制' onClick={() => copyToClipboard(formatJSON(detailLog.request_body))}
                      style={{ position: 'absolute', top: 12, right: 12, background: '#ffffff', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <Icon name='copy outline' />
                    </Button>
                  </div>
                </>
              )}

              {detailLog.upstream_request_body && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: 24 }}>
                    <Icon name='exchange' style={{ color: '#7c3aed', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>最终上游请求体</h4>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 24 }}>
                    <pre style={{
                      background: '#f5f3ff', padding: '16px 48px 16px 16px', borderRadius: 10,
                      border: '1px solid rgba(124,58,237,0.3)', maxHeight: 400, overflow: 'auto', fontSize: 13,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                      lineHeight: 1.6,
                    }}>
                      {formatJSON(detailLog.upstream_request_body)}
                    </pre>
                    <Button size='mini' icon title='复制' onClick={() => copyToClipboard(formatJSON(detailLog.upstream_request_body))}
                      style={{ position: 'absolute', top: 12, right: 12, background: '#ffffff', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <Icon name='copy outline' />
                    </Button>
                  </div>
                </>
              )}

              {/* 响应体 */}
              {detailLog.response_body && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                    marginTop: 24,
                  }}>
                    <Icon name='arrow left' style={{ color: detailLog.status === 'failed' ? '#dc2626' : '#16a34a', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>响应体</h4>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 24 }}>
                    <pre style={{
                      background: detailLog.status === 'failed' ? '#fef2f2' : '#f0fdf4',
                      padding: '16px 48px 16px 16px', borderRadius: 10,
                      border: detailLog.status === 'failed' ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(22,163,74,0.3)',
                      maxHeight: 400, overflow: 'auto', fontSize: 13,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                      lineHeight: 1.6,
                    }}>
                      {formatJSON(detailLog.response_body)}
                    </pre>
                    <Button size='mini' icon title='复制' onClick={() => copyToClipboard(formatJSON(detailLog.response_body))}
                      style={{ position: 'absolute', top: 12, right: 12, background: '#ffffff', border: detailLog.status === 'failed' ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(22,163,74,0.3)', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <Icon name='copy outline' />
                    </Button>
                  </div>
                </>
              )}

              {/* 调用示例代码 */}
              {(detailLog.request_url || detailLog.request_body) && (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 16,
                    marginTop: 24,
                  }}>
                    <Icon name='code' style={{ color: '#18181b', margin: 0 }} />
                    <h4 style={{ margin: 0, color: '#18181b', fontWeight: 600 }}>调用示例</h4>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    marginBottom: 16,
                    padding: '4px',
                    background: '#f5f5f5',
                    borderRadius: 10,
                    width: 'fit-content',
                  }}>
                    {[
                      { key: 'curl', icon: 'terminal', label: 'cURL' },
                      { key: 'python', icon: 'python', label: 'Python' },
                      { key: 'javascript', icon: 'js file outline', label: 'JavaScript' },
                    ].map((tab) => (
                      <Button
                        key={tab.key}
                        size='small'
                        onClick={() => setCodeTab(tab.key)}
                        style={{
                          borderRadius: 8,
                          background: codeTab === tab.key ? 'rgba(0,0,0,0.06)' : 'transparent',
                          boxShadow: codeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                          color: codeTab === tab.key ? '#18181b' : '#6b7280',
                          fontWeight: codeTab === tab.key ? 600 : 400,
                        }}
                      >
                        <Icon name={tab.icon} style={{ margin: '0 4px 0 0' }} /> {tab.label}
                      </Button>
                    ))}
                  </div>
                  {codeTab === 'curl' && renderCodeBlock(buildCurl(detailLog), 'curl')}
                  {codeTab === 'python' && renderCodeBlock(buildPython(detailLog), 'python')}
                  {codeTab === 'javascript' && renderCodeBlock(buildJavaScript(detailLog), 'javascript')}
                </>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions className='studio-logs-detail-modal__actions'>
          <Button onClick={() => setDetailModal(false)} style={{
            borderRadius: 8,
            padding: '8px 24px',
            fontWeight: 500,
          }}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ContextLogsTable;
